import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronsDownUp,
  ChevronsUpDown,
  CircleDollarSign,
  Copy,
  Download,
  ExternalLink,
  FileSpreadsheet,
  Layers,
  ListTree,
  MoreHorizontal,
  Package,
  Plus,
  Rows3,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  AddBomComponentsDialog,
  type BomAssemblyTarget,
} from '@/components/manufacturing/AddBomComponentsDialog';
import { CopyBomDialog } from '@/components/manufacturing/CopyBomDialog';
import { EditBomLineDialog } from '@/components/manufacturing/EditBomLineDialog';
import { ExportBomToSheetsDialog } from '@/components/manufacturing/ExportBomToSheetsDialog';
import {
  BomFlatView,
  BomSectionsView,
  BomTreeView,
  type BomNodeActions,
} from '@/components/manufacturing/BomViews';
import { useItem, useItemKinds } from '@/features/inventory/hooks';
import {
  bomTreeStats,
  bomTypesForItem,
  buildBomTree,
  canEditBoms,
  flatPartsList,
  flattenTree,
  subAssemblyKindSlugs,
  useBomGraph,
  useDeleteBom,
  useDeleteBomLine,
  useReorderBomLines,
  type BomGraph,
  type BomLine,
  type BomTreeNode,
} from '@/features/manufacturing/boms';
import { useAuth } from '@/app/AuthProvider';
import { bomExportTables, bomSheetTitle, downloadCsv } from '@/features/manufacturing/bomExport';
import { formatCurrency } from '@/lib/formatters';

type View = 'tree' | 'sections' | 'flat';

function sharedNotice(graph: BomGraph | undefined, assemblyItemId: string, rootItemId: string) {
  if (assemblyItemId === rootItemId) return undefined;
  const bom = graph?.byItemId.get(assemblyItemId);
  const name = bom?.item?.name ?? 'this sub-assembly';
  const uses = graph?.usedIn.get(assemblyItemId)?.size ?? 0;
  return `This changes the BOM of ${name}${uses > 1 ? `, which is used in ${uses} assemblies` : ''}. Every assembly that uses it picks up the change.`;
}

/**
 * Multi-level BOM builder for one assembly. The same exploded tree is
 * shown three ways: an indented tree, a sectioned table (one section
 * per sub-assembly), and a flattened pick list of leaf parts. Edits to
 * a nested line change that sub-assembly's own BOM, which is shared by
 * every assembly that uses it.
 */
export function BomDetailPage() {
  const { itemId = '' } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { role } = useAuth();
  const canEdit = canEditBoms(role);

  const { data: item, isLoading: itemLoading } = useItem(itemId);
  const { data: graph, isLoading: graphLoading } = useBomGraph();
  const { data: kinds } = useItemKinds();
  const subAssemblyKinds = useMemo(() => subAssemblyKindSlugs(kinds), [kinds]);
  const deleteLine = useDeleteBomLine();
  const reorder = useReorderBomLines();
  const deleteBom = useDeleteBom();

  const view: View = (['tree', 'sections', 'flat'] as const).includes(params.get('view') as View)
    ? (params.get('view') as View)
    : 'tree';
  const [buildQtyInput, setBuildQtyInput] = useState('1');
  const buildQty = Math.max(Number(buildQtyInput) || 1, 0.0001);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const [addTarget, setAddTarget] = useState<{ target: BomAssemblyTarget; notice?: string } | null>(
    null,
  );
  const [editing, setEditing] = useState<{
    line: BomLine;
    assembly: { itemId: string; sku: string; name: string };
    notice?: string;
  } | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(false);

  const rootTarget: BomAssemblyTarget | null = useMemo(
    () =>
      item ? { itemId: item.id, sku: item.sku, name: item.name, parentSku: item.parent_sku } : null,
    [item],
  );

  useEffect(() => {
    if (params.get('add') === '1' && rootTarget && canEdit) {
      setAddTarget({ target: rootTarget });
      const p = new URLSearchParams(params);
      p.delete('add');
      setParams(p, { replace: true });
    }
  }, [params, rootTarget, canEdit, setParams]);

  useEffect(() => {
    setCollapsed(new Set());
    setQuery('');
  }, [itemId]);

  const bom = graph?.byItemId.get(itemId) ?? null;
  const nodes = useMemo(() => buildBomTree(graph, itemId, buildQty), [graph, itemId, buildQty]);
  const allNodes = useMemo(() => flattenTree(nodes), [nodes]);
  const stats = useMemo(() => bomTreeStats(graph, itemId, nodes), [graph, itemId, nodes]);
  const parts = useMemo(() => flatPartsList(graph, itemId, nodes), [graph, itemId, nodes]);
  const exportTables = useMemo(() => bomExportTables(allNodes, parts), [allNodes, parts]);
  const sheetTitle = bomSheetTitle(item?.sku ?? 'BOM', buildQty);
  const totalCents = useMemo(() => nodes.reduce((acc, n) => acc + n.extCostCents, 0), [nodes]);

  const parents = useMemo(
    () =>
      [...(graph?.usedIn.get(itemId) ?? [])]
        .map((id) => graph?.byItemId.get(id)?.item)
        .filter((i): i is NonNullable<typeof i> => !!i)
        .sort((a, b) => a.sku.localeCompare(b.sku)),
    [graph, itemId],
  );

  // Search keeps matching rows plus every ancestor so the tree stays readable.
  const { matches, visibleKeys } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { matches: null, visibleKeys: null };
    const hit = new Set<string>();
    const visible = new Set<string>();
    const visit = (list: BomTreeNode[]): boolean => {
      let any = false;
      for (const n of list) {
        const self =
          (n.line.item?.sku ?? '').toLowerCase().includes(q) ||
          (n.line.item?.name ?? '').toLowerCase().includes(q);
        const child = visit(n.children);
        if (self) hit.add(n.key);
        if (self || child) {
          visible.add(n.key);
          any = true;
        }
      }
      return any;
    };
    visit(nodes);
    return { matches: hit, visibleKeys: visible };
  }, [nodes, query]);

  const subAssemblyKeys = useMemo(
    () => allNodes.filter((n) => n.children.length > 0).map((n) => n.key),
    [allNodes],
  );

  const setView = (v: string) => {
    const p = new URLSearchParams(params);
    if (v === 'tree') p.delete('view');
    else p.set('view', v);
    setParams(p, { replace: true });
  };

  const assemblyOf = (node: BomTreeNode) => {
    const owner = graph?.byItemId.get(node.parentItemId)?.item;
    return owner
      ? { itemId: owner.id, sku: owner.sku, name: owner.name }
      : { itemId: node.parentItemId, sku: item?.sku ?? '', name: item?.name ?? '' };
  };

  const actions: BomNodeActions = {
    canEdit,
    rootItemId: itemId,
    onAddInto: (node) => {
      if (!node) {
        if (rootTarget) setAddTarget({ target: rootTarget });
        return;
      }
      const part = node.line.item;
      if (!part) return;
      setAddTarget({
        target: { itemId: part.id, sku: part.sku, name: part.name, parentSku: part.parent_sku },
        notice: node.isSubAssembly
          ? sharedNotice(graph, part.id, itemId)
          : `${part.name} doesn’t have a BOM yet. Adding parts makes it a sub-assembly everywhere it’s used.`,
      });
    },
    onEdit: (node) =>
      setEditing({
        line: node.line,
        assembly: assemblyOf(node),
        notice: sharedNotice(graph, node.parentItemId, itemId),
      }),
    onMove: (node, direction) => {
      const siblings = graph?.byItemId.get(node.parentItemId)?.lines ?? [];
      const ids = siblings.map((l) => l.id);
      const from = ids.indexOf(node.line.id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= ids.length) return;
      [ids[from], ids[to]] = [ids[to], ids[from]];
      reorder.mutate(ids, {
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not reorder.'),
      });
    },
    onRemove: (node) => {
      const owner = assemblyOf(node);
      const label = node.line.item?.sku ?? 'this line';
      const shared =
        node.parentItemId !== itemId
          ? ` This edits ${owner.name}, which may be used elsewhere.`
          : '';
      if (!window.confirm(`Remove ${label} from ${owner.sku}?${shared}`)) return;
      deleteLine.mutate(node.line.id, {
        onSuccess: () => toast.success(`Removed ${label}`),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not remove the line.'),
      });
    },
  };

  const exportCsv = () => {
    if (!item) return;
    const table = view === 'flat' ? exportTables[1] : exportTables[0];
    if (!table) return;
    const filename =
      view === 'flat' ? `${item.sku}-parts-x${buildQty}.csv` : `${item.sku}-bom-x${buildQty}.csv`;
    downloadCsv(filename, table.rows);
  };

  const handleDeleteBom = () => {
    if (!bom || !item) return;
    const uses = parents.length;
    const msg =
      `Delete the BOM for ${item.sku}? Its ${bom.lines.length} line(s) and all versions are removed. The catalog item itself is kept.` +
      (uses ? ` It is still listed as a part on ${uses} other BOM(s); those lines stay.` : '');
    if (!window.confirm(msg)) return;
    deleteBom.mutate(bom.id, {
      onSuccess: () => {
        toast.success('BOM deleted');
        navigate('/mfg/boms');
      },
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Could not delete the BOM.'),
    });
  };

  const isLoading = itemLoading || graphLoading;
  const types = item ? bomTypesForItem(item, graph, subAssemblyKinds) : null;
  const eyebrow = types?.has('sub_assembly')
    ? 'Sub-assembly BOM'
    : types?.has('finished_good')
      ? 'Finished good BOM'
      : 'Bill of materials';
  const from = (location.state as { from?: unknown } | null)?.from;
  const backTo = typeof from === 'string' && from.startsWith('/mfg/boms') ? from : '/mfg/boms';

  if (!isLoading && !item) {
    return (
      <EmptyState
        icon={Package}
        title="Item not found"
        action={
          <Button asChild variant="outline">
            <Link to="/mfg/boms">Back to BOMs</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Bills of materials
      </Link>

      <PageHeader
        className="pb-0"
        eyebrow={eyebrow}
        title={item?.name ?? 'Loading…'}
        meta={
          item ? (
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-mono">{item.sku}</span>
              {bom?.version && (
                <Badge variant={bom.version.is_active ? 'success-soft' : 'muted'}>
                  v{bom.version.version}
                  {bom.version.is_active ? ' · active' : ''}
                </Badge>
              )}
              {item.color && <Badge variant="outline">{item.color}</Badge>}
              {item.region && <Badge variant="outline">{item.region}</Badge>}
              <Link
                to={`/catalog/items/${item.id}`}
                className="inline-flex items-center gap-1 text-info hover:underline"
              >
                Catalog item <ExternalLink className="h-3 w-3" />
              </Link>
            </span>
          ) : undefined
        }
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setSheetsOpen(true)}
              disabled={nodes.length === 0}
            >
              <FileSpreadsheet className="h-4 w-4" />
              Google Sheets
            </Button>
            {canEdit && (
              <>
                <Button variant="outline" onClick={() => setCopyOpen(true)} disabled={!item}>
                  <Copy className="h-4 w-4" />
                  Copy from…
                </Button>
                <Button
                  onClick={() => rootTarget && setAddTarget({ target: rootTarget })}
                  disabled={!item}
                >
                  <Plus className="h-4 w-4" />
                  Add components
                </Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More BOM actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setSheetsOpen(true)}
                  disabled={nodes.length === 0}
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Save to Google Sheets
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={exportCsv} disabled={nodes.length === 0}>
                  <Download className="h-4 w-4" />
                  Download {view === 'flat' ? 'parts list' : 'BOM'} (CSV)
                </DropdownMenuItem>
                {canEdit && bom && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-fresh-salmon-dark focus:text-fresh-salmon-dark"
                      onSelect={handleDeleteBom}
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete BOM
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {parents.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Used in:</span>
          {parents.map((p) => (
            <Link
              key={p.id}
              to={`/mfg/boms/${p.id}`}
              className="rounded-full border border-border bg-card px-2.5 py-0.5 text-xs hover:border-info hover:text-info"
              title={p.name}
            >
              <span className="font-mono">{p.sku}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard
          label="Direct lines"
          value={stats.directLines.toLocaleString()}
          icon={<Rows3 className="h-4 w-4" />}
          hint={stats.depth > 1 ? `${stats.depth} levels deep` : undefined}
          loading={isLoading}
        />
        <MetricCard
          label="Sub-assemblies"
          value={stats.subAssemblies.toLocaleString()}
          icon={<Layers className="h-4 w-4" />}
          loading={isLoading}
        />
        <MetricCard
          label="Distinct leaf parts"
          value={stats.uniqueParts.toLocaleString()}
          icon={<Package className="h-4 w-4" />}
          loading={isLoading}
        />
        <MetricCard
          label="Rolled-up cost / unit"
          value={nodes.length ? formatCurrency(Math.round(stats.unitCost.cents)) : '—'}
          icon={<CircleDollarSign className="h-4 w-4" />}
          hint={
            stats.unitCost.missing
              ? `${stats.unitCost.missing} part(s) with no PO or supplier cost`
              : 'Parts at their latest PO landed cost'
          }
          loading={isLoading}
        />
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="space-y-2 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : nodes.length === 0 ? (
        <EmptyState
          icon={ListTree}
          title="No components yet"
          action={
            canEdit ? (
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => rootTarget && setAddTarget({ target: rootTarget })}>
                  <Plus className="h-4 w-4" />
                  Add components
                </Button>
                <Button variant="outline" onClick={() => setCopyOpen(true)}>
                  <Copy className="h-4 w-4" />
                  Copy from another BOM
                </Button>
              </div>
            ) : undefined
          }
        />
      ) : (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-wrap items-center gap-3">
              <Tabs value={view} onValueChange={setView}>
                <TabsList>
                  <TabsTrigger value="tree" className="gap-1.5">
                    <ListTree className="h-4 w-4" />
                    Tree
                  </TabsTrigger>
                  <TabsTrigger value="sections" className="gap-1.5">
                    <Rows3 className="h-4 w-4" />
                    Sections
                  </TabsTrigger>
                  <TabsTrigger value="flat" className="gap-1.5">
                    <Package className="h-4 w-4" />
                    Flat parts list
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex min-w-[14rem] flex-1 items-center gap-2 rounded-md border border-border px-3 py-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a part in this BOM…"
                  className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                Build qty
                <Input
                  value={buildQtyInput}
                  onChange={(e) => setBuildQtyInput(e.target.value)}
                  inputMode="decimal"
                  className="h-9 w-20 text-right tabular-nums"
                  aria-label="Build quantity"
                />
              </label>
              {view === 'tree' && subAssemblyKeys.length > 0 && (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCollapsed(new Set())}
                    title="Expand all"
                  >
                    <ChevronsUpDown className="h-4 w-4" />
                    Expand
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCollapsed(new Set(subAssemblyKeys))}
                    title="Collapse all"
                  >
                    <ChevronsDownUp className="h-4 w-4" />
                    Collapse
                  </Button>
                </div>
              )}
            </div>

            {view === 'tree' && (
              <BomTreeView
                nodes={nodes}
                collapsed={collapsed}
                onToggle={(key) =>
                  setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
                }
                visibleKeys={visibleKeys}
                buildQty={buildQty}
                actions={actions}
                totalCents={totalCents}
              />
            )}
            {view === 'sections' && item && (
              <BomSectionsView
                nodes={nodes}
                rootName={item.name}
                rootSku={item.sku}
                buildQty={buildQty}
                actions={actions}
                matches={matches}
              />
            )}
            {view === 'flat' && <BomFlatView parts={parts} buildQty={buildQty} query={query} />}

            {stats.unitCost.missing > 0 && (
              <p className="text-xs text-muted-foreground">
                {stats.unitCost.missing} part{stats.unitCost.missing === 1 ? '' : 's'} with no PO or
                supplier cost. Add a supplier price on the part to cost it.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <ExportBomToSheetsDialog
        open={sheetsOpen}
        onOpenChange={setSheetsOpen}
        defaultTitle={sheetTitle}
        tables={exportTables}
      />

      {canEdit && (
        <>
          <AddBomComponentsDialog
            open={!!addTarget}
            onOpenChange={(open) => !open && setAddTarget(null)}
            target={addTarget?.target ?? null}
            notice={addTarget?.notice}
            graph={graph}
          />
          <EditBomLineDialog
            open={!!editing}
            onOpenChange={(open) => !open && setEditing(null)}
            line={editing?.line ?? null}
            assembly={editing?.assembly ?? null}
            notice={editing?.notice}
            graph={graph}
          />
          {rootTarget && (
            <CopyBomDialog
              open={copyOpen}
              onOpenChange={setCopyOpen}
              target={rootTarget}
              graph={graph}
            />
          )}
        </>
      )}
    </div>
  );
}
